/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { WhatsAppButton, WhatsAppContent } from "../types";
import { STUB_SHOP } from "../stub";
import { PhoneFrame, usePreview } from "../shared";
import { formatWhatsAppText } from "../richText";

// Colours of the WhatsApp UI itself (not our theme), so the preview reads as WhatsApp.
const WA = {
  header: "#075e54",
  chat: "#efe7dd",
  bubble: "#ffffff",
  meta: "#667781",
  link: "#00a5f4",
};

const BUTTON_ICON: Record<WhatsAppButton["type"], string> = {
  quick_reply: "reply",
  url: "open_in_new",
  copy_code: "content_copy",
};

export const WhatsAppPreview = defineComponent<{ content: WhatsAppContent }>({
  name: "WhatsAppPreview",
  props: ["content"],
  setup(props) {
    const { t } = useLocalization();
    const preview = usePreview();
    return () => {
      const c = props.content;
      return (
        <PhoneFrame background={WA.chat}>
          <div class="flex shrink-0 items-center gap-2 px-3 py-2 text-white" style={{ background: WA.header }}>
            <span class="material-symbols-outlined text-lg">arrow_back</span>
            <img src={STUB_SHOP.logo} class="h-8 w-8 rounded-full object-cover" />
            <div class="min-w-0 flex-1 leading-tight">
              <div class="flex items-center gap-1 truncate text-sm font-semibold">
                {STUB_SHOP.name}
                <span class="material-symbols-outlined text-[14px] text-[#25d366]">verified</span>
              </div>
              <div class="text-[11px] opacity-80">{t("campaigns.studio_whatsapp.business_account")}</div>
            </div>
          </div>
          <div class="min-h-0 flex-1 overflow-y-auto px-3 py-4">
            <div class="mx-auto mb-3 w-fit rounded-md bg-white/80 px-2 py-0.5 text-[11px]" style={{ color: WA.meta }}>
              {t("campaigns.studio_whatsapp.today")}
            </div>
            <div class="max-w-[92%]">
              <div class="overflow-hidden rounded-lg rounded-tl-none shadow-sm" style={{ background: WA.bubble }}>
                {c.header.type === "image" && <img src={c.header.src} class="aspect-[1.91/1] w-full object-cover p-1 pb-0 [border-radius:10px]" />}
                <div class="px-2.5 pb-1.5 pt-2 text-[13.5px] leading-snug text-[#111b21]">
                  {c.header.type === "text" && <div class="mb-1 font-bold">{preview.render(c.header.text)}</div>}
                  <div innerHTML={formatWhatsAppText(preview.render(c.body))} />
                  {c.footer && <div class="mt-1 text-[12px]" style={{ color: WA.meta }}>{c.footer}</div>}
                  <div class="mt-0.5 text-right text-[10px]" style={{ color: WA.meta }}>
                    09:41
                  </div>
                </div>
                {c.buttons.map((b) => (
                  <div
                    key={b.id}
                    class="flex items-center justify-center gap-1.5 border-t border-black/10 py-2 text-[13.5px] font-medium"
                    style={{ color: WA.link }}
                  >
                    <span class="material-symbols-outlined text-[16px]">{BUTTON_ICON[b.type]}</span>
                    {b.label || "…"}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div class="flex shrink-0 items-center gap-2 bg-[#f0f2f5] px-2 py-2">
            <div class="flex-1 rounded-full bg-white px-3 py-1.5 text-xs" style={{ color: WA.meta }}>
              {t("campaigns.studio_whatsapp.message_placeholder")}
            </div>
            <span class="flex h-8 w-8 items-center justify-center rounded-full bg-[#00a884] text-white">
              <span class="material-symbols-outlined text-base">mic</span>
            </span>
          </div>
        </PhoneFrame>
      );
    };
  },
});
